import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * ControlBar — the row that holds a screen's search, filters, scope, sort,
 * tools, status and actions. The CONTAINER, not the controls.
 *
 * WHY IT EXISTS. The control *vocabulary* already shipped — SortIndicator,
 * IconButton, FilterChip, Switch, Checkbox — but every screen still assembled
 * its own row around them. An audit found 38 control rows using 11 distinct
 * spacing signatures, only two of which read a token:
 *
 *     gap-2 no padding  ×17     gap-4 p-4  ×5     gap-3 no padding  ×4
 *     gap-3 p-4          ×3     gap-1.5    ×3     + 6 one-off signatures
 *
 * The same idiom disagreed with itself: the four attendance-history screens use
 * `gap-3 p-4`, SchedulePage uses `gap-3 p-3`, WorksiteDetailPage uses
 * `gap-4 p-4`. Nothing about those differences is a decision anyone made — they
 * are what happens when 38 screens each answer the same question separately.
 * So the answer moves here, once, and a screen can no longer get it wrong: it
 * declares WHAT goes in the bar, never where it sits or how far apart it is.
 *
 * ---------------------------------------------------------------------------
 * THE FIXED READING ORDER — the whole point of the component. UNCHANGED, and
 * two slots longer.
 *
 * Slots render search → filters → sort → tools → segmented → status → actions
 * REGARDLESS of the order the props are written in. That is the learnability
 * guarantee the owner asked for three times: once you have found the search box
 * on one screen, it is in the same place on all 38. A prop order can't quietly
 * relocate it, and a screen that passes `actions` first still gets its search
 * first.
 *
 * The anatomy is a sentence and each slot's position in it is the meaning:
 *
 *     search │ Filters ▾ │ sort │ icon tools │ ⟶ segmented view switcher │ status
 *
 * `segmented` IS THE TRAILING SLOT, and that moved. It used to sit beside
 * `filters` on the reasoning that a scope switcher is a filter worth one tap —
 * true about its meaning and wrong about where a reader looks for it. A
 * segmented strip is the only control in the bar whose position was not fixed
 * in practice, because the slots before it are optional: with a search box the
 * strip sat mid-bar, without one it sat at the leading edge, and the ERP
 * screen (no search, no sort) put it first of all. Fourteen screens, three
 * apparent positions, all of them "correct" — which is the drift this component
 * exists to stop. Pinned to the trailing edge it is in ONE place on all
 * fourteen whatever else the screen does or does not have, and the trade is
 * that the group it reads with is now a group it no longer sits next to.
 *
 * `tools` sits after the filtering group because refresh and column-layout
 * answer "how am I looking at them", which is a different question and comes
 * second. `sort` stays inside that group: it is a refinement of the row set,
 * not of the view of it, and moving it would relocate a control on the ~6
 * screens that sort from the bar for no gain.
 *
 * ---------------------------------------------------------------------------
 * ONE SPACING SIGNATURE, AND IT IS NOW ONE DISTANCE PLUS A HAIRLINE.
 *
 * This is the substantive change in the warm-paper pass, so here is what moved
 * and why. The bar used to carry TWO distances:
 *
 *     BETWEEN GROUPS   `gap-component` (16px)   WITHIN A GROUP  `gap-element` (8px)
 *
 * …on the reasoning that search, filters, sort, status and actions are five
 * separate things and 16px is what tells the eye so. That reasoning was right
 * about the goal and wrong about the mechanism. At 16px between groups the bar
 * reads as five controls that happen to share a card, and the handoff's bar is
 * one 52px object with things inside it — which is what lets a reader treat the
 * whole strip as "the controls" rather than hunting through it. Adding two more
 * slots made the old signature worse, not merely different: seven groups at
 * 16px is a 100px-wider bar that wraps on a laptop.
 *
 * So the inline distance is now ONE step — `gap-control` (`--gap-control`, 6px,
 * the token that exists for exactly this bar and nothing else) — and the job
 * 16px was doing is done by a HAIRLINE between bands. A 1px rule is a stronger
 * group boundary than 10px of extra air and costs no width at all, which is the
 * trade the handoff makes and the migration doc records ("`--gap-control`,
 * hairline dividers between groups").
 *
 * THREE BANDS, and the dividers fall between them, never inside one:
 *
 *     search  │  filters · sort  │  tools     ⟶  segmented · status · actions
 *     what am    which rows am I     how am I      the trailing edge: which
 *     I after    looking at          looking       VIEW I am in, what it holds,
 *                                    at them       and what I can do about it
 *
 * Only rendered slots count, so a bar with search and actions gets exactly one
 * divider and a bar with one slot gets none. There is no way for a screen to
 * add, remove or move one.
 *
 * THE ROW GAP IS STILL 8px, and deliberately not 6. `gap-y-element` is the
 * distance between LINES of a wrapped bar, and a line is not a band — 6px
 * between two stacked 38px controls glues them into a block, which is the one
 * thing the old comment on `gap-component` got right about vertical space. So
 * the signature is `gap-x-control gap-y-element`: tight along the row, breathing
 * between rows.
 *
 * PADDING is `py-1.5 ps-1.5 pe-2` (6px block, 6px leading, 8px trailing) rather
 * than the old `p-4 md:p-tile` (16/20px). `--pad-tile` is named for "stat tile,
 * FILTER BAR, KPI card" and the bar is no longer that object: at 20px of padding
 * a 38px control needs a 78px bar, and the handoff's is 52px. The extra 2px on
 * the trailing edge is the handoff's own asymmetry and it is not arbitrary — the
 * leading edge holds the search field's recessed fill, which reads as inset, and
 * the trailing edge holds bordered controls, which read as sitting on top; equal
 * padding makes the trailing one look tighter than it is.
 *
 * SHAPE AND ELEVATION. `rounded-toolbar` (14px — the radius role that exists for
 * this component and the popovers that hang off it) and a hairline border, which
 * is load-bearing now: the page/card step is 1.043:1, so the border plus the
 * shadow is what makes this read as an object at all.
 *
 * The shadow is `shadow-[shadow:var(--shadow-resting),var(--shadow-hover)]` —
 * the resting contact edge plus a soft drop, which is the handoff's two-layer
 * treatment for a bar that floats slightly over the page it filters. Composed
 * from the two EXISTING roles rather than written as a literal, so it re-cuts
 * itself for dark along with the rest of the ramp (dark reverts the ink to true
 * black at ~3× the alpha; a warm-ink shadow on a warm-ink page is nothing at
 * all). A hand-written `0 8px 24px -18px rgb(27 26 33 / .3)` would have been the
 * mock's exact second layer and would also have been the one shadow in the app
 * that stays invisible in dark mode.
 *
 * NOTE THE `shadow:` TYPE HINT, which is not decoration. Without it Tailwind
 * cannot tell a two-layer `var()` list from a shadow COLOUR, guesses colour, and
 * emits `--tw-shadow-color` plus `--tw-shadow: var(--tw-shadow-colored)` — a
 * rule that sets no shadow at all. Compiled and checked: the hinted form emits
 * `--tw-shadow: var(--shadow-resting),var(--shadow-hover)` through the normal
 * ring/shadow composition, the unhinted one emits nothing usable, and the
 * failure is silent either way.
 *
 * A MIN-HEIGHT AND NOT A HEIGHT. 52px is the floor; the bar grows when it wraps
 * or when a control inside it does. `Button.tsx` established that anatomy and the
 * handoff's README states it as the rule for this migration: where a mock and a
 * primitive disagree on geometry, the primitive's anatomy wins and the mock's
 * values win.
 *
 * WHAT IS NOT HERE. The handoff's bar is sticky at `top: 56px` inside a
 * gradient-backed wrapper that gains `--shadow-sticky` once the content scrolls
 * past 12px. That is shell geometry, not the toolbar's — it needs the scroll
 * position and the header height — and it belongs to `AppShell`. This component
 * stays a bar that can be put anywhere.
 *
 * ---------------------------------------------------------------------------
 * HOW IT WRAPS — this is PageHeader's lesson (apps/web/src/components/common/
 * PageHeader.tsx), applied rather than re-learned. UNCHANGED.
 *
 * `flex-wrap` + `ms-auto`, NOT `justify-between` + `shrink-0`. A `shrink-0`
 * cluster is sized to its children's MAX-CONTENT width — and the max-content
 * width of a wrapping container is its items summed UNWRAPPED — so a cluster
 * that says `flex-wrap` can never actually wrap; it overflows, and the squeeze
 * lands entirely on whichever sibling is allowed to shrink. Here every group
 * wraps internally and none is pinned, so a bar too wide for its line breaks
 * onto a second one instead of running off the edge.
 *
 * Note the DELIBERATE absence of `min-w-0` on the groups: without it a group's
 * automatic minimum is its min-content width, i.e. its widest single control,
 * so a cramped bar re-flows onto more lines and never squashes a field into
 * illegibility. Desktop is untouched — with room to spare nothing wraps and
 * nothing shrinks.
 *
 * `ms-auto` goes on the first of status/actions that is present, so the
 * trailing cluster is pinned to the trailing edge whether it shares the search
 * box's line or has one of its own. A bar with only search and actions
 * therefore looks deliberate — search leading, actions trailing — rather than
 * lopsided.
 *
 * RTL: no physical direction anywhere. `ms-auto`, `ps-*`/`pe-*`, `gap` and
 * `flex-wrap` all resolve along the inline axis, so under `dir="rtl"` the whole
 * bar mirrors and the actions stay on the trailing edge. Never `ml-auto`,
 * `pl-*`/`pr-*` or `right-*` here.
 * ======================================================================== */

/**
 * The ONE width for a ControlBar's search field — put it on the `<Input>` you
 * pass as `search`:
 *
 *     <ControlBar search={<Input className={controlBarSearchClass} …/>} … />
 *
 * The owner's complaint was that fields are different sizes from screen to
 * screen, and a search box is the field they compared: today the app's search
 * inputs are variously `w-full`, `w-64`, `max-w-sm`, `flex-1` and unset, so no
 * two screens agree. This is the shared answer — `--search-width` from the token
 * layer, full width on a phone so a search box spans the device it is hardest to
 * type on.
 *
 * It is a class and not a wrapper because `search` takes any node: a plain
 * `<Input>`, an input with a leading icon, or a `<Combobox>`. All three want
 * the same width and none of them want the same markup.
 *
 * Deliberately NOT applied by the component itself. Search is `ReactNode`, so
 * the bar cannot reach into it to add a class without cloning the element —
 * which would break the moment a screen wraps its input in a `<div>` for an
 * icon. An exported constant the adopter spreads is the honest version.
 *
 * THE MIGRATION IT WAS WAITING FOR IS DONE. `<SearchField>` is the handoff's
 * richer version of the same slot — a recessed fill, a leading magnifier, a
 * `⌘K` hint, and `min-w-search flex-search` in place of this definite width, so
 * it flexes instead of forcing the wrap — and as of the toolbar pass EVERY
 * screen's search is one: the team directory was the last holdout, and the rule
 * is now written down (docs/design-rules.md §2, "The search box is
 * SearchField").
 *
 * This export stays, and not out of politeness. `search` takes any node, and a
 * slot that holds something other than a screen's search box — a `<Combobox>`
 * that picks a report, an input with its own leading affordance — still wants
 * the one width without wanting the search anatomy. What it must no longer be
 * is a screen's search field.
 */
export const controlBarSearchClass = 'w-full sm:w-search';

/** The bar itself. See the header note for why these utilities and no others. */
const BAR_CLASS = cn(
  'flex flex-wrap items-center gap-x-control gap-y-element',
  'min-h-[3.25rem] rounded-toolbar border border-border bg-surface py-1.5 ps-1.5 pe-2',
  'shadow-[shadow:var(--shadow-resting),var(--shadow-hover)]'
);

/**
 * One slot's group. Wraps internally and is allowed to shrink (no `shrink-0`,
 * no `min-w-0` — see the header note), so a group with four filter chips in it
 * re-flows instead of overflowing.
 *
 * `gap-control` inside, matching the bar: the whole strip is one distance now,
 * and the boundaries between bands are carried by the hairline instead.
 */
const GROUP_CLASS = 'flex flex-wrap items-center gap-control';

/**
 * The three bands the dividers fall between — see the header note. A slot's band
 * is a property of the slot, not of what a screen put in it, so this map is the
 * single place the grouping is decided.
 */
const BAND: Record<SlotName, 0 | 1 | 2> = {
  search: 0,
  filters: 1,
  sort: 1,
  tools: 2,
  segmented: 2,
  status: 2,
  actions: 2,
};

/** The fixed reading order. This array IS the guarantee. */
const ORDER = ['search', 'filters', 'sort', 'tools', 'segmented', 'status', 'actions'] as const;

type SlotName = (typeof ORDER)[number];

/**
 * The hairline between two bands.
 *
 * It is rendered as the FIRST CHILD of the group that opens a band rather than
 * as a sibling between the two groups, and that is load-bearing: the trailing
 * cluster carries `ms-auto`, so a divider sitting outside it would be left
 * behind at the leading edge with the auto-margin's gap opening up after it. As
 * the group's own first child it travels with the group to the trailing edge.
 *
 * `aria-hidden` and no text: it is a visual boundary, and the grouping it draws
 * is already carried for assistive tech by each control's own label.
 */
function Divider(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-control-bar-divider=""
      className="h-6 w-px flex-none self-center bg-border"
    />
  );
}

/**
 * Is there anything in this slot worth a group box?
 *
 * `ReactNode` includes the values React renders as NOTHING — `undefined`,
 * `null`, `false` and `''` — and call sites reach for every one of them
 * (`{canExport && <Button/>}` yields `false`, not `undefined`). A bare
 * `{slot && …}` truthiness test would leave an empty group in the flex row,
 * and an empty group still consumes a gap: the bar would grow a hole exactly
 * where a conditional control isn't — and, now, a hairline with nothing on
 * either side of it.
 */
function filled(slot: ReactNode): boolean {
  return slot != null && slot !== false && slot !== '';
}

export interface ControlBarProps {
  /** Free-text search. Rendered FIRST, always in the same place. */
  search?: ReactNode;
  /** Filters — a `<FiltersPopover>`, or FilterChips on a screen not yet moved. */
  filters?: ReactNode;
  /**
   * The range / scope / view switcher — a `<Segmented>`. Rendered at the
   * TRAILING edge on every screen, whatever else the bar holds: see the header
   * note for why one fixed corner beats sitting beside the filters it reads
   * with.
   */
  segmented?: ReactNode;
  /** Sort control, when the screen sorts from the bar rather than table headers. */
  sort?: ReactNode;
  /**
   * Icon tools — `<IconTool>`s for refresh and column layout. These change how
   * the rows are shown, not which rows they are, which is why they sit after the
   * filtering group and behind a hairline.
   */
  tools?: ReactNode;
  /** A short result count / status line, trailing, before actions. */
  status?: ReactNode;
  /** Primary + secondary page actions. Pinned to the TRAILING edge. */
  actions?: ReactNode;
  className?: string;
}

export function ControlBar({
  search,
  filters,
  segmented,
  sort,
  tools,
  status,
  actions,
  className,
}: ControlBarProps): JSX.Element {
  const slots: Record<SlotName, ReactNode> = {
    search,
    filters,
    sort,
    tools,
    segmented,
    status,
    actions,
  };
  const present = ORDER.filter((name) => filled(slots[name]));

  // `ms-auto` belongs to whichever trailing slot comes FIRST, so the CLUSTER
  // travels together on the trailing edge. Putting it on each of them would
  // push them to opposite ends of the bar, which is the `justify-between` bug
  // in another costume. `segmented` joined the cluster and is first in it, so
  // a bar with a view switcher and an export button pins both, together, and
  // the switcher does not drift to wherever the optional slots left it.
  const autoSlot: SlotName =
    filled(segmented) ? 'segmented' : filled(status) ? 'status' : 'actions';

  return (
    <div className={cn(BAR_CLASS, className)} data-control-bar="">
      {present.map((name, i) => {
        const previous = present[i - 1];
        // A divider opens a band, so it needs a band before it to divide from.
        const opensBand = previous !== undefined && BAND[name] > BAND[previous];
        return (
          <div
            key={name}
            data-control-bar-slot={name}
            className={cn(
              GROUP_CLASS,
              // `basis-full` below `sm` gives the search box a whole line on a
              // phone, which is also what makes `w-full` inside it resolve
              // against a definite width instead of the group's own
              // shrink-to-fit.
              name === 'search' && 'basis-full sm:basis-auto',
              // The status line is the bar's one piece of prose, and it is meta
              // rather than a control — quieter type, tertiary ink.
              name === 'status' && 'text-copy-sm text-foreground-tertiary',
              name === autoSlot && 'ms-auto'
            )}
          >
            {opensBand && <Divider />}
            {slots[name]}
          </div>
        );
      })}
    </div>
  );
}
